import { AdminOperatorRunHealth } from "@domain/ai_operator"
import useTranslate from "@hooks/use_translate"
import { Chip, Skeleton, Stack, Tooltip, Typography } from "@mui/material"

import { formatMs, formatUsd } from "./operatorRun.utils"

type Props = {
  health?: AdminOperatorRunHealth
  onOpenRun: (runId: string) => void
}

export default function OperatorSummary({ health, onOpenRun }: Props) {
  const { translate } = useTranslate()
  if (!health) return <Skeleton width={360} />

  const unanswered =
    health.byOutcome.find((row) => row.outcome === "UNANSWERED")?.runs ?? 0
  const rate = (count: number) =>
    health.runs ? ` (%${Math.round((count / health.runs) * 100)})` : ""
  const stat = (value: string, label: string, color?: string) => (
    <Typography variant="body2" color={color ?? "text.secondary"}>
      <b>{value}</b> {label}
    </Typography>
  )

  return (
    <Stack gap={1}>
      <Stack direction="row" gap={2.5} flexWrap="wrap">
        {stat(String(health.runs), translate("ai_operator_health_runs"))}
        {stat(
          `${health.failed}${rate(health.failed)}`,
          translate("ai_operator_health_failed"),
          health.failed ? "error.dark" : undefined
        )}
        {stat(
          `${unanswered}${rate(unanswered)}`,
          translate("ai_operator_health_unanswered"),
          unanswered ? "warning.dark" : undefined
        )}
        {stat(
          formatMs(health.avgDurationMs),
          translate("ai_operator_health_avg_duration")
        )}
        {stat(formatUsd(health.costUsd), translate("ai_operator_cost"))}
      </Stack>
      {health.errors.length || health.reasons.length ? (
        <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
          {health.errors.map((row) => (
            <Tooltip key={`${row.code}-${row.stage}`} title={row.message}>
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`${row.code} · ${row.stage} ×${row.runs}`}
                onClick={() => onOpenRun(row.runId)}
              />
            </Tooltip>
          ))}
          {health.reasons.map((row) => (
            <Chip
              key={row.reason}
              size="small"
              color="warning"
              variant="outlined"
              label={`${translate(`ai_operator_reason_${row.reason}`, row.reason)} ×${row.runs}`}
              onClick={() => onOpenRun(row.runId)}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  )
}
